import axios from "axios";
import { getLocalStorageValue, decodeToken, getAccessToken, saveResponseInLocalStorage } from "./localStorage";
import { PostRequest } from "./RequestTemplates";
import { getClientId } from "../shared/flavours/useFlavour";

export type AuthenticationResponse = {
  access_token: string;
  impersonated_access_token?: string;
  auto_login_token: string;
  jti: string;
  refresh_token: string;
  scope: string;
  token_type: string;
  user_id: string;
  impersonatedBy?: string;
};

export type RegisterFlagsT = {
  regOnlyNaviFlag?: boolean;
  regOfferFlag?: boolean;
  regWsFlag?: boolean;
  regLightWsFlag?: boolean;
  regAnonLightWsFlag?: boolean;
  regReminderFlag?: boolean;
  gtmEvent?: Record<string, string>;
  queryParams?: string;
};

export type QueriesT = {
  signInUrl?: string;
  latestDeviceMetadata?: string;
};

export let tmpStorage: undefined | AuthenticationResponse = undefined;

const clientId = getClientId();

export const checkLogin = async (email: string) => {
  return (
    axios
      // .get(`${domain}/api/checkLogin?email=${encodeURIComponent(email)}`)
      .get(`${process.env.API_SERVER_URL}/users/checkLogin?emailAddress=${encodeURIComponent(email)}`)
      .then((response) => {
        return response.status;
      })
      .catch((error) => {
        return error.response.status;
      })
  );
};

export const login = async (username: string, password: string) => {
  const params: Record<string, string> = {
    username: encodeURIComponent(username),
    password: encodeURIComponent(password),
    grant_type: "password",
    scope: "read write",
    token_type: "bearer",
    clientId: clientId || "",
  };

  const queryString: string = Object.keys(params)
    .map((key) => key + "=" + params[key])
    .join("&");

  return axios
    .post<AuthenticationResponse>(`${process.env.API_SERVER_URL}/login`, queryString, {
      auth: {
        username: process.env.API_USER_NAME || "",
        password: process.env.API_PASSWORD || "",
      },
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    })
    .then((response) => {
      try {
        saveResponseInLocalStorage(response.data);
      } catch (e) {
        console.error("Cannot save login response in lcoal storage, access denied.");
        tmpStorage = response.data;
      }
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
};

export const refreshToken = async (token: string) => {
  const params: Record<string, string> = {
    grant_type: "refresh_token",
    refresh_token: token,
  };

  const queryString: string = Object.keys(params)
    .map((key) => key + "=" + params[key])
    .join("&");

  return axios
    .post(`${process.env.API_SERVER_URL}/login`, queryString, {
      auth: {
        username: decodeToken(getAccessToken())?.client_id || "",
        password: process.env.API_PASSWORD || "",
      },
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    })
    .then(({ data }: { data: AuthenticationResponse }) => {
      try {
        saveResponseInLocalStorage(data);
      } catch (e) {
        console.error("Cannot save login response in lcoal storage, access denied.");
        tmpStorage = data;
      }
      return data;
    });
};

export const socialLogin = async (
  platform: "google" | "facebook",
  accessToken: string,
  flags: RegisterFlagsT,
  queries: QueriesT
) => {
  const url = new URL(`${process.env.API_SERVER_URL}/sociallogin/${platform}`);

  const params: { [key: string]: string | undefined | boolean | Record<string, string> } = {
    social_access_token: accessToken,
    clientId: clientId,
    ...flags,
    ...queries,
  };

  const utmParams = ["utmSource", "utmMedium", "utmCampaign", "partnerId", "utmReferrer", "utmExpid", "utmTerm"];

  utmParams.forEach((key) => {
    const value = getLocalStorageValue(key);

    if (value) params[key] = typeof value === 'object' ? value.value : value;
  });

  for (const [key, value] of Object.entries(params)) {
    if (!!value) url.searchParams.append(key, value.toString());
  }

  if (platform === "facebook" || platform === "google") {
    return axios
      .get<AuthenticationResponse>(url.href)
      .then((response) => {
        saveResponseInLocalStorage(response.data);
        return response.data;
      })
      .catch((error) => {
        throw new Error(error);
      });
  }
  throw new Error("Invalid social platform");
};

export const resetPassword = async (email: string) => {
  return PostRequest(`${process.env.API_SERVER_URL}/password/reset`, { email: email && email.trim() });
};

export const changePassword = async (token: string, password: string) => {
  return PostRequest(`${process.env.API_SERVER_URL}/password/update`, { token, password: password && password.trim() });
};

export async function updatePassword(newPassword: string) {
  await PostRequest(`${process.env.API_SERVER_URL}/password/profile-update`, { password: newPassword?.trim() });
}
